import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getComplianceCerts, saveComplianceCert, GetComplianceCertsOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ShieldCheck, Plus, AlertTriangle, Clock, CheckCircle2, XCircle, Search } from 'lucide-react';
import { isManagerPlus } from '../lib/roles';

type Cert = GetComplianceCertsOutputType['certificates'][0];

export default function ComplianceCertificates() {
  const { user } = useAuth();
  const [data, setData] = useState<GetComplianceCertsOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [addDialog, setAddDialog] = useState(false);
  const [form, setForm] = useState({ certificateName: '', certificateType: '', issuedDate: '', expiryDate: '', notes: '' });

  useEffect(() => { if (user) load(); }, [user]);
  const load = () => { setLoading(true); getComplianceCerts({}).then(setData).finally(() => setLoading(false)); };

  const manager = isManagerPlus(user?.role);
  const today = new Date().toISOString().split('T')[0];
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];

  const certs = (data?.certificates || []).filter((c: any) => !search || (c.certificateName || '').toLowerCase().includes(search.toLowerCase()));
  const expiring = certs.filter((c: any) => c.expiryDate && c.expiryDate > today && c.expiryDate <= in30);
  const expired = certs.filter((c: any) => c.expiryDate && c.expiryDate <= today);
  const verified = certs.filter((c: any) => c.verificationStatus === 'Verified');
  const pending = certs.filter((c: any) => c.verificationStatus === 'Pending');

  const handleAdd = async () => {
    if (!form.certificateName) return;
    try {
      await saveComplianceCert({ action: 'create', ...form });
      toast.success('Certificate added');
      setAddDialog(false);
      setForm({ certificateName: '', certificateType: '', issuedDate: '', expiryDate: '', notes: '' });
      load();
    } catch { toast.error('Failed'); }
  };

  const handleVerify = async (cert: any, status: string) => {
    try {
      await saveComplianceCert({ action: 'verify', id: cert.id, certificateName: cert.certificateName, verificationStatus: status });
      toast.success(`Certificate ${status.toLowerCase()}`);
      load();
    } catch { toast.error('Failed'); }
  };

  const handleViewFile = async (cert: any) => {
    await saveComplianceCert({ action: 'viewFile', certificateName: cert.certificateName });
    if (cert.certificateFile?.[0]?.url) window.open(cert.certificateFile[0].url, '_blank');
  };

  const statusBadge = (s?: string) => {
    const colors: Record<string, string> = { Verified: 'bg-green-500/10 text-green-700', Pending: 'bg-yellow-500/10 text-yellow-700', Rejected: 'bg-red-500/10 text-red-700', Expired: 'bg-red-500/10 text-red-700' };
    return <Badge className={colors[s || ''] || 'bg-muted text-muted-foreground'}>{s || 'Unknown'}</Badge>;
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-primary" /> Compliance Certificates</h1>
          <p className="text-sm text-muted-foreground mt-1">{manager ? 'Manage and verify staff compliance certificates' : 'Your compliance certificates'}</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-48" />
          </div>
          <Button size="sm" onClick={() => setAddDialog(true)}><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-green-600">{verified.length}</p><p className="text-xs text-muted-foreground">Verified</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-yellow-600">{pending.length}</p><p className="text-xs text-muted-foreground">Pending</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-orange-600">{expiring.length}</p><p className="text-xs text-muted-foreground">Expiring Soon</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-red-600">{expired.length}</p><p className="text-xs text-muted-foreground">Expired</p></CardContent></Card>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All ({certs.length})</TabsTrigger>
          <TabsTrigger value="expiring">Expiring ({expiring.length})</TabsTrigger>
          <TabsTrigger value="expired">Expired ({expired.length})</TabsTrigger>
          {manager && <TabsTrigger value="pending">Pending ({pending.length})</TabsTrigger>}
        </TabsList>

        {['all', 'expiring', 'expired', 'pending'].map(tab => (
          <TabsContent key={tab} value={tab} className="space-y-3 mt-4">
            {(tab === 'all' ? certs : tab === 'expiring' ? expiring : tab === 'expired' ? expired : pending).map((c: any) => (
              <Card key={c.id}>
                <CardContent className="py-4 flex flex-col sm:flex-row justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{c.certificateName}</h3>
                      {statusBadge(c.verificationStatus)}
                      {c.expiryDate && c.expiryDate <= today && <Badge variant="destructive" className="text-xs"><AlertTriangle className="w-3 h-3 mr-1" />Expired</Badge>}
                      {c.expiryDate && c.expiryDate > today && c.expiryDate <= in30 && <Badge className="bg-orange-500/10 text-orange-700 text-xs"><Clock className="w-3 h-3 mr-1" />Expiring</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{c.certificateType} · Issued: {c.issuedDate ? new Date(c.issuedDate).toLocaleDateString() : '—'} · Expires: {c.expiryDate ? new Date(c.expiryDate).toLocaleDateString() : '—'}</p>
                    {c.verifiedBy && <p className="text-xs text-muted-foreground mt-1">Verified by {c.verifiedBy} on {c.verifiedDate ? new Date(c.verifiedDate).toLocaleDateString() : '—'}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.certificateFile && data?.canViewDetails && <Button size="sm" variant="outline" onClick={() => handleViewFile(c)}>View File</Button>}
                    {manager && c.verificationStatus === 'Pending' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleVerify(c, 'Verified')} className="text-green-600"><CheckCircle2 className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => handleVerify(c, 'Rejected')} className="text-red-600"><XCircle className="w-4 h-4" /></Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {(tab === 'all' ? certs : tab === 'expiring' ? expiring : tab === 'expired' ? expired : pending).length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No certificates</p>}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Certificate</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Certificate name" value={form.certificateName} onChange={e => setForm({ ...form, certificateName: e.target.value })} />
            <Select value={form.certificateType} onValueChange={v => setForm({ ...form, certificateType: v })}>
              <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>{['Food Hygiene Level 2','Allergen Awareness','Personal Licence','First Aid','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Issue Date</label><Input type="date" value={form.issuedDate} onChange={e => setForm({ ...form, issuedDate: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Expiry Date</label><Input type="date" value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} /></div>
            </div>
            <Input placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button><Button onClick={handleAdd}>Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
